import { Building2, Clock, ShieldCheck, type LucideIcon } from "lucide-react"

import { IconTile, Tag, type Tone } from "@/components/superadmin/kit"
import { Switch } from "@/components/ui/switch"
import { ALL_MODULES, MODULE_META, normalizeModuleSelection, type AppModule } from "@/lib/modules"
import { cn } from "@/lib/utils"

export const MODULE_ICON: Record<AppModule, LucideIcon> = {
  full_itacondo: Building2,
  access_control: ShieldCheck,
  time_clock: Clock,
}

export const MODULE_TONE: Record<AppModule, Tone> = {
  full_itacondo: "accent",
  access_control: "violet",
  time_clock: "success",
}

/** Chips compactos para tabelas e cabeçalhos. */
export function ModuleTags({ modules, className }: { modules: AppModule[]; className?: string }) {
  const list = normalizeModuleSelection(modules)
  return (
    <div className={cn("flex flex-wrap gap-1", className)}>
      {list.map((m) => <Tag key={m} tone={MODULE_TONE[m]}>{MODULE_META[m].short}</Tag>)}
    </div>
  )
}

/**
 * Lista de módulos com switches. A suíte completa inclui os outros dois, que
 * aparecem ligados e travados enquanto ela estiver ativa.
 */
export function ModulePicker({
  value,
  onChange,
  disabled,
}: {
  value: AppModule[]
  onChange: (modules: AppModule[]) => void
  disabled?: boolean
}) {
  const full = value.includes("full_itacondo")

  const toggle = (module: AppModule, on: boolean) => {
    if (module === "full_itacondo") {
      // Desligar a suíte mantém acesso e ponto, para o superadmin tirar o que não quer.
      onChange(on ? ["full_itacondo"] : ["access_control", "time_clock"])
      return
    }
    const parts: AppModule[] = full ? ["access_control", "time_clock"] : value.filter((m) => m !== "full_itacondo")
    const next = on ? [...parts, module] : parts.filter((m) => m !== module)
    if (next.length === 0) return // precisa de pelo menos um
    onChange(normalizeModuleSelection(next))
  }

  return (
    <ul className="divide-y divide-border rounded-lg border border-border">
      {ALL_MODULES.map((m) => {
        const implied = full && m !== "full_itacondo"
        const checked = implied || value.includes(m)
        const only = !full && checked && value.length === 1
        return (
          <li key={m} className="flex items-center gap-3 px-3 py-2.5">
            <IconTile icon={MODULE_ICON[m]} tone={checked ? MODULE_TONE[m] : "neutral"} />
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-medium text-foreground">{MODULE_META[m].label}</div>
              <div className="text-[12px] text-muted-foreground">
                {implied ? "Incluído na suíte completa · desligue para tirar a suíte" : MODULE_META[m].description}
              </div>
            </div>
            <Switch
              checked={checked}
              disabled={disabled || only}
              onCheckedChange={(on) => toggle(m, on)}
              aria-label={MODULE_META[m].label}
            />
          </li>
        )
      })}
    </ul>
  )
}
